import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  startTransition,
} from "react";
import axios, { AxiosError } from "axios";
import { CircleX, TriangleAlert } from "lucide-react";
import styled from "styled-components";
import * as Ariakit from "@ariakit/react";
import Button from "../../components/button/button";
import {
  Select,
  SelectItem,
  SelectProvider,
} from "../../components/form/select";
import SubmitButton from "../../components/form/submit";
import { Loader } from "../../components/loader";
import ResetCss from "../../components/ResetCss";
import { Color } from "../../constants/color";
import { Icons } from "../../constants/icons";

import {
  BaseCourse,
  Certificate,
  CertificateNotice,
  CertificateUploadResult,
  CertificateValidationResult,
  CorsoType,
} from "../../types";
import { BE_BASE_URL, HTTP_TIMEOUT_CARICAMENTO_MS } from "../../utils/config";
import { useTranslation } from "react-i18next";
import { useSelectStore } from "@ariakit/react";
import { axiosInstance } from "../../provider/authProvider";
import { descriviAvvisi } from "../../features/certificate/avvisi";
import { messaggioDiErrore } from "../../features/notifiche/avvisi";
import { toast, ToastContainer, Bounce, ToastOptions } from "react-toastify";
import { matchSorter } from "match-sorter";
import VisorePdf from "../../components/pdf/VisorePdf";
import DialogConferma from "../../components/modal/conferma";
import Suggerimento from "../../components/tooltip/suggerimento";
import { useSolaLettura } from "../../features/user/solaLettura";

/*
 Gli avvisi che portano un elenco — scartati, segnalazioni, rifiutati — hanno un problema
 che gli altri toast non hanno: un PDF di venti pagine diventa venti attestati, e quindi
 un messaggio di venti righe che va letto riga per riga, non intravisto.

 Da qui le tre differenze rispetto a un avviso qualunque:
 - `pre-line`, altrimenti gli a capo dell'elenco si perdono e le voci finiscono ammassate
   in un paragrafo unico (era il difetto piu' evidente: il testo li ha sempre avuti);
 - nessuna chiusura automatica: dieci secondi bastano per una frase, non per un elenco che
   l'operatore deve confrontare con i documenti che ha in mano. Si chiude quando ha finito;
 - altezza massima con scorrimento, perche' un elenco lungo non scavalchi lo schermo.

 Il testo scuro sul giallo non si decide qui ma in `src/index.css`: e' la palette dei toast,
 e vale per chiunque ne apra uno.
*/
const AVVISO_CON_ELENCO: ToastOptions = {
  position: "top-right",
  autoClose: false,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: "colored",
  transition: Bounce,
  style: { whiteSpace: "pre-line", maxHeight: "60vh", overflowY: "auto" },
};

/*
 Il caricamento da Excel e il download del template restano nel codice ma non si mostrano:
 decisione del 2026-09-15, il percorso normale della pagina e' il trascinamento dei PDF.
 Non sono stati rimossi perche' la sospensione e' dichiarata provvisoria: si riaccendono
 rimettendo `true`, e finche' questa costante e' `false` quel percorso non e' collaudato da nessuno.
 */
const MOSTRA_CARICAMENTO_EXCEL = false;

const ClickWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
`;

/*
 La pagina occupa l'altezza della finestra e non cresce oltre: chi valida cinquanta attestati
 deve vedere insieme la lista da cui sceglie, i campi che compila e il documento da cui li copia.
 Lo scorrimento vive dentro le singole colonne, non sulla pagina.
 */
/*
 Le sole intestazioni della pagina erano gli <h2> delle sezioni: senza un <h1> la
 schermata non ha un titolo da annunciare, e la gerarchia parte dal secondo livello.
 Il titolo resta fuori dalla vista perche' a schermo non c'e' posto — l'altezza della
 pagina e' calcolata — ma resta nell'albero di accessibilita'.
*/
const TitoloPagina = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: 0 20px 12px;
  background-color: white;
  min-height: 0;
  overflow: hidden;
`;

/*
 La barra di navigazione lascia sotto di se' 50px di aria (`NavShadow`,
 src/components/navbar/index.tsx): e' lo spazio del titolo, che le altre pagine hanno e
 questa non ha piu'. Qui se ne recupera quasi tutta, perche' ogni pixel speso in alto e' un
 pixel in meno per il documento da leggere. I 10px che restano non sono un residuo: la barra
 e' `sticky` e proietta un'ombra verso il basso, e senza quelli il bordo tratteggiato del box
 ci finisce sotto.

 Va applicato inline e non nello styled-component, perche' `reset.css` porta
 `div[role="reset"] * { margin: 0 }`, che per specificita' batte una classe.
 */
const RECUPERO_ARIA_NAVBAR = "-40px";

/* La fascia di caricamento: il box del trascinamento e, accanto, il bottone che elabora. */
const FasciaCaricamento = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.8rem;
  flex: 0 0 auto;
`;

/*
 Un box solo per il trascinamento e per i file in coda: erano due riquadri separati e
 sembravano due cose diverse, mentre il secondo e' il risultato del primo.
 */
const BoxCaricamento = styled.label<{ $sopra: boolean }>`
  display: block;
  position: relative;
  border: 2px dashed
    ${({ $sopra }) => ($sopra ? Color.GREEN : Color.LIGHT_BLUE)};
  background-color: ${({ $sopra }) => ($sopra ? "#f2f9ec" : "white")};
  border-radius: 12px;
  padding: 0.6rem 1rem;
  cursor: pointer;
  transition:
    border-color 0.15s ease-in-out,
    background-color 0.15s ease-in-out;
`;

const ZonaTrascinamento = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  color: ${Color.BLUE};
  font-family: "Avenir Next", sans-serif;
  font-weight: bold;
  font-size: 0.95rem;
  padding: 0.3rem 0;

  & > .tenue {
    font-weight: normal;
    color: ${Color.LIGHT_BLUE};
  }
`;

const ConteggioInCoda = styled.span`
  background-color: ${Color.BLUE};
  color: white;
  border-radius: 999px;
  padding: 0.1rem 0.6rem;
  font-size: 0.75rem;
`;

/* I file in coda, dentro lo stesso box: altezza limitata, il resto si raggiunge scorrendo. */
const FileInCoda = styled.ul`
  list-style-type: none;
  margin: 0.5rem 0 0.1rem;
  padding: 0.4rem 0 0;
  border-top: 1px solid ${Color.LIGHT_GREY};
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  max-height: 5.5rem;
  overflow-y: auto;
`;

const PastigliaFile = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background-color: #eef4fb;
  border: 1px solid #dbe8f5;
  border-radius: 999px;
  padding: 0.1rem 0.4rem 0.1rem 0.7rem;
  font-size: 0.75rem;
  color: ${Color.BLUE};
  max-width: 18rem;

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/*
 Le tre colonne della lavorazione: elenco, modulo, documento. Affiancate e non impilate,
 perche' impilate il documento non entra mai in schermo insieme ai campi da compilare.
 */
const Colonne = styled.div`
  display: grid;
  grid-template-columns: minmax(14rem, 20rem) minmax(20rem, 26rem) minmax(
      0,
      1fr
    );
  gap: 1rem;
  flex: 1 1 auto;
  min-height: 0;
`;

const ColonnaLista = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: 0.5rem;
`;

/*
 Il motivo dell'errore sta sotto il campo che lo riguarda, non in un riquadro a parte:
 l'operatore legge la spiegazione dove deve mettere le mani. Prima c'era un triangolo di
 45 pixel accanto al titolo, con il testo nascosto in un fumetto al passaggio del mouse:
 rubava spazio in cima, non diceva quale campo fosse sbagliato, e la spiegazione si trovava
 solo per caso.
 */
const NotaCampo = styled.p`
  color: ${Color.RED};
  font-family: "Avenir Next", sans-serif;
  font-weight: normal;
  font-size: 0.78rem;
  line-height: 1.3;
  margin: 0.25rem 0 0;
  padding-left: 1rem;
`;

/* I campi del modulo che possono portare una nota di errore sotto di se'. */
type CampoConNota =
  | "cf"
  | "corso"
  | "dataValidita"
  | "inizioCorso"
  | "fineCorso";

/*
 A quale campo si riferisce un messaggio di errore del backend.

 Il legame e' sul **testo** del messaggio, ed e' il punto debole di questa mappatura: se il
 backend riscrive una frase, la regola smette di riconoscerla. Non e' pero' un legame
 fragile in modo pericoloso, perche' il caso "non riconosciuto" e' previsto e innocuo —
 il messaggio torna nell'avviso volante, come prima, e non si perde mai.

 L'alternativa pulita e' che il backend dica **lui** quale campo ha rifiutato, invece di
 affidarlo a una frase in italiano. Vale la pena farlo quando si mettera' mano agli errori
 per singolo attestato; fino ad allora questa e' la lettura piu' onesta possibile da qui.

 L'ordine conta: «la data di inizio corso e' successiva a quella di fine corso» nomina
 entrambe le date, e il campo da segnare e' il primo, che e' il soggetto della frase.
 */
const CAMPO_PER_MESSAGGIO: { indizio: RegExp; campo: CampoConNota }[] = [
  { indizio: /codice fiscale/i, campo: "cf" },
  {
    indizio: /corso non registrato|non registrato nel sistema/i,
    campo: "corso",
  },
  { indizio: /data di inizio corso/i, campo: "inizioCorso" },
  { indizio: /data di scadenza/i, campo: "dataValidita" },
  { indizio: /data di fine corso/i, campo: "fineCorso" },
];

const campoDelMessaggio = (messaggio: string): CampoConNota | null =>
  CAMPO_PER_MESSAGGIO.find(({ indizio }) => indizio.test(messaggio))?.campo ??
  null;

/*
 Che cosa non va, per ciascuno stato di aggancio che il backend assegna all'attestato
 estratto: -1 discente non censito, -2 corso non a catalogo, -3 tutti e due,
 -4 codice fiscale illeggibile, -5 codice fiscale illeggibile e corso non a catalogo.
 Il testo dice il fatto e l'azione, perche' un avviso che non dice che fare e' rumore.

 I due testi sul codice fiscale sono la ragione per cui -4 e -5 esistono: chiedono cose
 opposte — rimettere gli occhi sul documento, oppure censire una persona — e prima erano
 un messaggio solo che le offriva entrambe lasciando indovinare all'operatore. Il backend
 sa quale delle due e', perche' calcola il carattere di controllo del codice fiscale.
 */
const CF_NON_CENSITO =
  "Nessun dipendente di questa azienda ha questo codice fiscale. Registra il dipendente in anagrafica o correggi il codice fiscale.";
const CF_ILLEGGIBILE =
  "Questo non è un codice fiscale valido: sull'attestato manca oppure è stato letto male. Controlla il documento e correggilo.";
const CORSO_NON_A_CATALOGO =
  "Il codice corso è assente o non corrisponde a nessun corso a catalogo. Scegli il corso dall'elenco.";

const NOTE_PER_STATO: Record<number, { cf?: string; corso?: string }> = {
  [-1]: { cf: CF_NON_CENSITO },
  [-2]: { corso: CORSO_NON_A_CATALOGO },
  [-3]: { cf: CF_NON_CENSITO, corso: CORSO_NON_A_CATALOGO },
  [-4]: { cf: CF_ILLEGGIBILE },
  [-5]: { cf: CF_ILLEGGIBILE, corso: CORSO_NON_A_CATALOGO },
};

/*
 Un attestato e' da sistemare quando il backend gli ha dato uno stato negativo, qualunque
 esso sia. Enumerare gli stati uno per uno fa sparire la segnalazione in silenzio ogni volta
 che se ne aggiunge uno: e' esattamente quello che sarebbe successo con -4 e -5.
 */
const daSistemare = (stato: number | undefined) => (stato ?? 0) < 0;

/** Gli stati in cui il problema riguarda (anche) il corso. */
const corsoDaScegliere = (stato: number | undefined) =>
  stato === -2 || stato === -3 || stato === -5;

const InputField = styled.div`
  font-family: "Avenir Next", sans-serif;
  font-weight: bold;
  color: ${Color.BLUE};
  display: inline-flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 2rem;

  & > label {
    padding-left: 1rem;
    padding-bottom: 0.2rem;
  }

  & > input {
    padding: 0.5rem 1rem;
    border: 2px solid ${Color.LIGHT_BLUE};
    border-radius: 1rem;
    font-size: 1rem;
  }

  & > textarea {
    padding: 0.5rem 1rem;
    border: 2px solid ${Color.LIGHT_BLUE};
    border-radius: 1rem;
    font-size: 1rem;
    overflow-y: auto;
  }
`;

const SubTitle = styled.h2`
  color: var(--blue300);
  font-family:
    Avenir Next Bold,
    sans-serif;
  font-size: 30px;
  font-weight: bold;
  padding-left: 10px;
  text-align: center;
`;

const UploadBtnWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const ButtonWrapper = styled.div`
  display: flex;
  text-align: center;
  justify-content: end;
  margin: 1rem;
  gap: 1rem;
`;

/* Modulo e documento, affiancati dentro la seconda e la terza colonna. */
const DataWrapper = styled.div`
  display: contents;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-collapse: collapse;
  flex: 1 1 auto;
  min-height: 0;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  padding: 0 0.3rem;

  overflow: hidden;
  ::-webkit-scrollbar {
    position: relative;
    display: flex;
    left: 4rem;
    width: 0.6rem;
    border-radius: 4rem;
  }
  ::-webkit-scrollbar-button {
    display: none;
  }
  ::-webkit-scrollbar-track {
    border-radius: 4rem;
    background-color: ${Color.LIGHT_BLUE};
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 4rem;
    background-color: ${Color.LIGHT_BLUE};
  }
  ::-webkit-scrollbar-track-piece {
    border-radius: 4rem;
    background-color: ${Color.GREY};
  }
`;

/*
 In colonna stretta le quattro informazioni non stanno su una riga sola: cognome e nome
 sopra, corso e ente formatore sotto, come nelle righe di un elenco e non di una tabella.
 */
const BaseTable = styled.div`
  display: grid;
  grid-template-columns: 1.2rem minmax(0, auto) minmax(0, 1fr);
  gap: 0 0.5rem;
  padding: 0.5rem 0.6rem;
  align-items: start;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.6rem;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  color: var(--blue300);
  font-family:
    Avenir Next,
    sans-serif;
  font-weight: bold;
  border-bottom: 0.15rem solid ${Color.LIGHT_BLUE};
  flex: 0 0 auto;
`;

const ListWrapper = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  padding-bottom: 0.3rem;
`;

const TableRow = styled(BaseTable)<{ $state: number; $selected: boolean }>`
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  color: ${Color.LIGHT_BLUE};
  text-transform: uppercase;
  border-radius: 8px;
  border-left: 3px solid transparent;
  margin: 0 0.2rem 0.2rem;

  /* La casella di spunta accompagna tutte e tre le righe dell'elemento. */
  & > div:first-child {
    grid-row: 1 / span 3;
    align-self: center;
  }
  & > p:nth-of-type(1) {
    grid-column: 2;
    grid-row: 1;
    font-weight: bold;
    font-size: 0.85rem;
    color: ${Color.BLACK};
  }
  & > p:nth-of-type(2) {
    grid-column: 3;
    grid-row: 1;
    font-weight: bold;
    font-size: 0.85rem;
    color: ${Color.BLACK};
  }
  & > p:nth-of-type(3) {
    grid-column: 2 / span 2;
    grid-row: 2;
    font-size: 0.75rem;
  }
  & > p:nth-of-type(4) {
    grid-column: 2 / span 2;
    grid-row: 3;
    font-size: 0.7rem;
    opacity: 0.85;
  }
  & > p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  border-left-color: ${({ $state }) =>
    daSistemare($state) ? Color.RED : "transparent"};

  background-color: ${({ $selected, $state }) => {
    return daSistemare($state)
      ? $selected
        ? "#FBDEDC"
        : "#FDECEB"
      : $selected
        ? Color.LIGHT_GREY
        : "transparent";
  }};

  &:hover {
    background-color: ${({ $state }) => {
      return daSistemare($state) ? "#FBDEDC" : Color.LIGHT_GREY;
    }};
  }
`;

const SectionWrapper = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 16px;
  min-height: 0;
`;

const LeftSectionWrapper = styled(SectionWrapper)`
  overflow-y: auto;
  padding: 14px 16px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  background-color: white;
`;

const RightSectionWrapper = styled(SectionWrapper)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 10px 12px;
  overflow: hidden;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  background-color: white;
`;

const PdfPreviewWrapper = styled.div`
  flex: 1 1 auto;
  min-height: 0;
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const PreviousCertificatesPage: React.FC = () => {
  const { t } = useTranslation();
  // In sola lettura (abbonamento disattivato, ODOO 300) la coda si consulta, ma non si carica,
  // non si valida e non si scarta nulla: il backend rifiuterebbe.
  const solaLettura = useSolaLettura();
  const [pdfPreview, setPdfPreview] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<
    { file: File; fileType: string }[]
  >([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const updateFileInputRef = useRef<HTMLInputElement | null>(null);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [selectedCertificate, setSelectedCertificate] =
    useState<Certificate | null>(null);
  const [selectedCertificateIds, setSelectedCertificateIds] = useState<
    number[]
  >([]);

  /*
   L'ultimo rifiuto del backend, gia' attribuito a un campo. Vive solo finche' resta vero:
   si cancella appena si cambia attestato o si tocca il campo contestato.
   */
  const [erroreSalvataggio, setErroreSalvataggio] = useState<{
    campo: CampoConNota;
    messaggio: string;
  } | null>(null);
  /*
   Il modulo gia' letto, in attesa che l'operatore confermi nella dialog. Finche' e' pieno
   la dialog e' aperta; si svuota sia dopo la validazione sia se si rinuncia.
   */
  const [validazioneInAttesa, setValidazioneInAttesa] = useState<{
    formData: FormData;
    file: File | null;
  } | null>(null);
  /*
   L'operazione di lotto in attesa di conferma. Validare e eliminare agiscono su tutta la
   selezione e non si tornano indietro: entrambe passano dalla stessa dialog, che cambia
   testo e colore del bottone a seconda di quale delle due sia.
   */
  const [azioneDiLotto, setAzioneDiLotto] = useState<
    "validazione" | "eliminazione" | null
  >(null);
  const [courses, setCourses] = useState<Map<number, CorsoType> | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<CorsoType | null>(null);
  const [searchCourseValue, setSearchCourseValue] = useState("");

  const matches = useMemo(() => {
    const coursesArray = Array.from(courses?.values() || []);
    return matchSorter(coursesArray, searchCourseValue, {
      keys: ["code", "name"],
      threshold: matchSorter.rankings.CONTAINS,
    });
  }, [courses, searchCourseValue]);

  //console.log("matches", matches);

  const [loading, setLoading] = useState({
    show: true,
    message: "Caricamento in corso...",
  });

  const courseStore = useSelectStore({
    value: `${selectedCourse?.id || ""}`,
    setValue: (value) => {
      setSelectedCourse(courses?.get(+value) || null);
    },
  });

  const emptyFormData = useMemo(
    () => ({
      nome: "",
      cognome: "",
      cf: "",
      titolo: "",
      codiceCorso: "",
      dataValidita: "",
      enteFormatore: "",
      codiceAttestato: "",
      inizioCorso: "",
      fineCorso: "",
    }),
    [],
  );

  /*
   Il file scelto per la sostituzione vive nel DOM, non nello stato: azzerare la sola
   anteprima lasciava il campo con scritto il nome di un PDF che il visore non mostrava
   piu' e che nessuno aveva piu' intenzione di caricare. Le due cose si dimenticano
   insieme.
   */
  const scordaFileScelto = () => {
    if (updateFileInputRef.current) {
      updateFileInputRef.current.value = "";
    }
    setPdfPreview(null);
  };

  const resetForm = () => {
    setFormData(emptyFormData); // Resetta lo stato dei dati del form
    if (updateFileInputRef.current) {
      updateFileInputRef.current.value = ""; // Resetta il campo file
    }
  };
  const [formData, setFormData] = useState(emptyFormData);

  const fetchCertificates = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${BE_BASE_URL}/certificates/imported`,
      );
      setCertificates(response.data);
    } catch (e) {
      toast.error(
        messaggioDiErrore(e, "Errore durante il recupero dei documenti."),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        },
      );
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      const response = await axiosInstance.get<
        Omit<BaseCourse, "variationId">[]
      >(`${BE_BASE_URL}/courses`);
      if (response.data) {
        const courses: Map<number, CorsoType> = new Map<number, CorsoType>();
        response.data.forEach((c: Omit<BaseCourse, "variationId">) =>
          courses.set(c.id, {
            id: c.id,
            code: c.code,
            name: c.name,
          }),
        );
        setCourses(courses);
      }
    } catch (e) {
      toast.error(
        messaggioDiErrore(e, "Errore durante il recupero dei documenti."),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        },
      );
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchCertificates(), fetchCourses()]).finally(() =>
      setLoading({ show: false, message: "" }),
    );
  }, [fetchCertificates, fetchCourses]);

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance({
        url: BE_BASE_URL + "/certificates/download", // Il link del tuo endpoint backend
        method: "GET",
        responseType: "blob", // Questo è necessario per ricevere il file binario
      });

      // Creare un link temporaneo per scaricare il file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Attestato-Template.xlsx"); // Nome del file da scaricare
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      // console.error("Errore durante il download del template:", error);
    }
  };

  const renderCourseSelectItems = useCallback(
    () =>
      matches.map((value) => (
        <SelectItem
          key={`corso_${value.id}`}
          value={`${value.id}`}
          render={
            <Ariakit.ComboboxItem
              render={
                <p key={`corso_item_${value.id}`}>
                  [{value.code}] {value.name}
                </p>
              }
            />
          }
        />
      )),
    [matches],
  );

  /*
   Apre la finestra di scelta per il percorso Excel, che e' un bottone e non un `<label>`.
   Il percorso normale — i PDF — non passa di qui: quel box e' un `<label>` e la finestra la
   apre il browser.
   */
  const apriSceltaExcel = () => fileInputRef.current?.click();

  const [trascinamentoSopra, setTrascinamentoSopra] = useState(false);

  const contenitoreRef = useRef<HTMLDivElement | null>(null);
  const [altezzaPagina, setAltezzaPagina] = useState<string>("auto");

  /*
   La pagina deve stare in una schermata sola, ma l'altezza disponibile dipende
   dall'intestazione dell'applicazione, che vive fuori da questo componente e non ha
   un'altezza fissa da sottrarre in CSS. La si misura dove la pagina comincia, e la si
   rimisura quando la finestra cambia.
   */
  useLayoutEffect(() => {
    const misura = () => {
      const elemento = contenitoreRef.current;
      if (!elemento) return;
      const disponibile =
        window.innerHeight - elemento.getBoundingClientRect().top - 8;
      setAltezzaPagina(`${Math.max(disponibile, 420)}px`);
    };
    misura();
    window.addEventListener("resize", misura);
    return () => window.removeEventListener("resize", misura);
  }, []);

  /*
   Unica porta d'ingresso della coda: ci passano sia i file trascinati sia quelli scelti
   con la finestra di sistema, cosi' le due strade si comportano allo stesso modo.

   L'estensione va controllata qui e non basta `accept` sull'input: quello che arriva per
   trascinamento non passa dall'input, e `accept` e' comunque solo un suggerimento che
   l'utente puo' aggirare scegliendo «tutti i file».

   Ogni file che non entra in coda produce un messaggio. Il silenzio e' la cosa peggiore:
   l'utente clicca, sceglie un file, non vede niente e conclude che il caricamento e' rotto.
   */
  const accodaFile = (files: File[], tipo: "pdf" | "xlsx") => {
    const estensioni = tipo === "pdf" ? [".pdf"] : [".xls", ".xlsx"];
    const ammessi = files.filter((file) =>
      estensioni.some((estensione) =>
        file.name.toLowerCase().endsWith(estensione),
      ),
    );

    const formatoSbagliato = files.length - ammessi.length;
    if (formatoSbagliato > 0) {
      const atteso = tipo === "pdf" ? "un PDF" : "un foglio Excel";
      toast.warn(
        formatoSbagliato === 1
          ? `Un file non è ${atteso} e non è stato aggiunto.`
          : `${formatoSbagliato} file non sono ${atteso} e non sono stati aggiunti.`,
        { transition: Bounce },
      );
    }
    if (!ammessi.length) return;

    /*
     I doppioni si contano qui e non dentro `setUploadedFiles`: la funzione di aggiornamento
     dev'essere pura, e in sviluppo React la esegue due volte — un avviso li' dentro
     comparirebbe due volte.
     */
    const giaInCoda = ammessi.filter((file) =>
      uploadedFiles.some((caricato) => caricato.file.name === file.name),
    );
    if (giaInCoda.length) {
      toast.info(
        giaInCoda.length === 1
          ? `«${giaInCoda[0].name}» è già in coda.`
          : `${giaInCoda.length} file erano già in coda.`,
        { transition: Bounce },
      );
    }

    setUploadedFiles((prevFiles) => {
      const nuovi = ammessi
        .filter(
          (file) =>
            !prevFiles.some((caricato) => caricato.file.name === file.name),
        )
        .map((file) => ({ file, fileType: tipo }));
      return nuovi.length ? [...prevFiles, ...nuovi] : prevFiles;
    });
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setTrascinamentoSopra(false);
    accodaFile(Array.from(event.dataTransfer.files ?? []), "pdf");
  };

  interface ErrorResponse {
    message: string;
  }

  const uploadFiles = async () => {
    if (solaLettura || !uploadedFiles.length) return;
    const uploadedCertificates: Certificate[] = [];
    const scartati: CertificateNotice[] = [];
    const segnalazioni: CertificateNotice[] = [];

    for (let i = 0; i < uploadedFiles.length; i++) {
      const { file, fileType } = uploadedFiles[i];
      const formData = new FormData();
      formData.append("files", file);
      formData.append("fileType", fileType);

      setLoading({ show: true, message: "Elaborazione in corso..." });
      try {
        const response = await axiosInstance.post(
          `${BE_BASE_URL}/certificates/imported`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            // L'estrazione dei metadati e' lenta: qui serve piu' del timeout di default.
            timeout: HTTP_TIMEOUT_CARICAMENTO_MS,
          },
        );

        if (response.status >= 400) {
          toast.error(
            "Errore imprevisto, riprovare e se il problema persiste contattare l'assistenza.",
            {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: "colored",
              transition: Bounce,
            },
          );
        }
        const esito: CertificateUploadResult = response.data;
        uploadedCertificates.push(...esito.caricati);
        scartati.push(...esito.scartati);
        segnalazioni.push(...esito.segnalazioni);
      } catch (error) {
        const axiosError = error as AxiosError;
        // Un timeout non porta con se' una risposta: senza questo ramo finirebbe nel messaggio
        // generico di errore di rete, che non dice all'operatore che cosa e' successo al file.
        if (axiosError.code === "ECONNABORTED") {
          toast.error(t("attestati-caricamento-timeout", { file: file.name }), {
            position: "top-right",
            autoClose: 8000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
          });
        } else if (axiosError.response) {
          const errorData = axiosError.response.data as ErrorResponse;
          toast.error(errorData.message || "Errore durante il caricamento.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
          });
        } else {
          // In caso di errore di rete o altro
          toast.error("Errore di rete o errore imprevisto.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
          });
        }
      } finally {
        setLoading({ show: false, message: "" });
      }
    }

    // Gli scartati vanno detti: se restano solo nel log del server, chi carica vede sparire le
    // righe senza un messaggio e conclude che il file non sia stato acquisito.
    if (scartati.length > 0) {
      const intestazione = t("attestati-caricamento-scartati", {
        count: scartati.length,
      });
      toast.warning(
        `${intestazione}
${descriviAvvisi(scartati)}`,
        AVVISO_CON_ELENCO,
      );
    }

    // Le segnalazioni riguardano attestati ENTRATI: vanno guardati prima di validarli.
    if (segnalazioni.length > 0) {
      const intestazione = t("attestati-caricamento-segnalazioni", {
        count: segnalazioni.length,
      });
      toast.warning(
        `${intestazione}
${descriviAvvisi(segnalazioni)}`,
        AVVISO_CON_ELENCO,
      );
    }
    setCertificates((prev) => [...prev, ...uploadedCertificates]);
    setUploadedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /*
   La validazione e' il punto di non ritorno: l'attestato entra in archivio con i dati che
   si vedono a schermo, letti dall'AI e corretti a mano. Si chiede conferma prima, e per
   farlo il modulo si legge subito e si mette da parte: la dialog non blocca il thread come
   window.confirm, e quando l'operatore risponde il form non e' piu' raggiungibile
   dall'evento.
   */
  const chiediConfermaValidazione = (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (solaLettura) return;
    const modulo = event.currentTarget;
    const fileInput = modulo.querySelector<HTMLInputElement>(
      'input[type="file"][name="file"]',
    );
    setValidazioneInAttesa({
      formData: new FormData(modulo),
      file: fileInput?.files && fileInput.files[0] ? fileInput.files[0] : null,
    });
  };

  const updateCertificate = async (formData: FormData, file: File | null) => {
    const idValue = formData.get("certificate-id");
    if (typeof idValue === "string") {
      const id = Number(idValue);

      const updatedData = {
        nome: formData.get("nome") as string,
        cognome: formData.get("cognome") as string,
        codiceFiscaleDiscente: formData.get("codiceFiscaleDiscente") as string,
        titoloCorso: selectedCourse?.name || "",
        codiceCorso: selectedCourse?.code || "",
        dataValidita: formData.get("dataValidita") as string,
        enteFormatore: formData.get("enteFormatore") as string,
        codiceAttestato: formData.get("codiceAttestato") as string,
        inizioCorso: formData.get("inizioCorso") as string,
        fineCorso: formData.get("fineCorso") as string,
        file, // Usa il file solo se non è null
      };

      const submitData = new FormData();
      submitData.append("nome", updatedData.nome);
      submitData.append("cognome", updatedData.cognome);
      submitData.append(
        "codiceFiscaleDiscente",
        updatedData.codiceFiscaleDiscente,
      );
      submitData.append("titoloCorso", updatedData.titoloCorso);
      submitData.append("codiceCorso", updatedData.codiceCorso);
      submitData.append("dataValidita", updatedData.dataValidita);
      submitData.append("enteFormatore", updatedData.enteFormatore);
      submitData.append("codiceAttestato", updatedData.codiceAttestato);
      submitData.append("inizioCorso", updatedData.inizioCorso);
      submitData.append("fineCorso", updatedData.fineCorso);

      if (file) {
        submitData.append("file", file);
      }

      let success = true;
      let errorMessage = "";
      try {
        // Effettua la richiesta per aggiornare il certificato
        await axiosInstance
          .put(`${BE_BASE_URL}/certificates/imported/${id}`, updatedData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .catch(function (error) {
            /*
           // console.error(
              "Errore durante la validazione dell'attestato:",
              error
            );
            */
            success = false;
            errorMessage =
              error.response.data.message ||
              "Errore imprevisto durante la validazione dell'attestato.";
          });
        // Refresh lista attestati con gli eventuali dati aggiornati
        await fetchCertificates();
        if (success) {
          // Se validazione ok reset del form
          setSelectedCertificate(null);
          resetForm();
          courseStore.setValue("");
          setPdfPreview(null);
          setSelectedCourse(null);
          toast.success("Attestato aggiornato con successo.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
          });
        }
      } catch (e) {
        success = false;
        errorMessage = messaggioDiErrore(
          e,
          "Errore imprevisto durante la validazione dell'attestato.",
        );
      } finally {
        if (success) {
          setErroreSalvataggio(null);
        } else {
          /*
           Se il messaggio riguarda un campo, si mostra sotto quel campo e resta li' finche'
           non e' stato corretto. Altrimenti resta l'avviso volante: meglio effimero che
           perduto.
           */
          const campo = campoDelMessaggio(errorMessage);
          if (campo) {
            setErroreSalvataggio({ campo, messaggio: errorMessage });
          } else {
            setErroreSalvataggio(null);
            toast.error(errorMessage, {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: "colored",
              transition: Bounce,
            });
          }
        }
      }
    } else {
      // console.error("ID dell'attestato non trovato o non è valido.");
      toast.error("ID dell'attestato non valido.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  /*
   Le note seguono l'attestato scelto invece di vivere in uno stato a parte: erano due
   verita' da tenere allineate a mano, e bastava un percorso che non azzerasse il messaggio
   per mostrare l'errore di un attestato sopra i dati di un altro.

   Sopra le note che derivano dallo stato si posa l'eventuale rifiuto del salvataggio, che
   e' piu' recente e piu' preciso: parla di quello che l'operatore ha appena scritto.
   */
  const noteCampi: Partial<Record<CampoConNota, string>> = {
    ...(NOTE_PER_STATO[selectedCertificate?.stato ?? 0] ?? {}),
    ...(erroreSalvataggio
      ? { [erroreSalvataggio.campo]: erroreSalvataggio.messaggio }
      : {}),
  };

  /*
   Correggere il campo contestato fa sparire la nota: lasciarla mentre l'operatore riscrive
   proprio quel valore vorrebbe dire accusarlo di un errore che sta gia' rimediando.
   */
  const dimenticaErroreSu = (campo: CampoConNota) => {
    if (erroreSalvataggio?.campo === campo) setErroreSalvataggio(null);
  };

  const handleRowSelect = (cert: Certificate) => {
    setSelectedCertificate(cert);
    scordaFileScelto();
    // Il rifiuto riguardava l'attestato precedente: su questo non significa niente.
    setErroreSalvataggio(null);
    setFormData({
      nome: cert.nome || "",
      cognome: cert.cognome || "",
      cf: cert.codiceFiscaleDiscente || "",
      titolo: cert.titoloCorso || "",
      codiceCorso: cert.codiceCorso || "",
      dataValidita: cert.dataValidita || "",
      enteFormatore: cert.enteFormatore || "",
      codiceAttestato: cert.codiceAttestato || "",
      inizioCorso: cert.inizioCorso || "",
      fineCorso: cert.fineCorso || "",
    });
    // cerco fra i courses quello con il codice corso uguale a cert.codiceCorso
    const course = Array.from(courses?.values() || []).find(
      (c) =>
        cert.codiceCorso &&
        c.code.toLowerCase() === cert.codiceCorso.toLowerCase(),
    );
    // Se lo trovo imposto il corso selezionato
    if (course) {
      courseStore.setValue(`${course.id}`);
      setSelectedCourse(course);
    } else {
      courseStore.setValue("");
      setSelectedCourse(null);
    }
    setPdfPreview(
      cert.fileData ? `data:application/pdf;base64,${cert.fileData}` : null,
    );
  };

  const formWrapperRef = useRef<HTMLFormElement | null>(null); // Riferimento per il modulo di aggiornamento

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedCertificateIds([]); // Deseleziona tutti
    } else {
      const allIds = certificates
        .filter((cert) => cert.stato === 1)
        .map((cert) => cert.id); // Prendi tutti gli ID dai certificati validi
      setSelectedCertificateIds(allIds); // Seleziona tutti
    }
    setAllSelected(!allSelected); // Inverti lo stato di allSelected
  };

  const toggleCertificateSelection = (id: number) => {
    setSelectedCertificateIds((prevSelectedIds) =>
      prevSelectedIds.includes(id)
        ? prevSelectedIds.filter((selectedId) => selectedId !== id)
        : [...prevSelectedIds, id],
    );
  };

  const validateAll = async () => {
    if (solaLettura) return;
    if (selectedCertificateIds.length === 0) {
      toast.info("Seleziona almeno un certificato da validare.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
      return;
    }
    setAzioneDiLotto("validazione");
  };

  const validaSelezionati = async () => {
    try {
      // Trova i dati dei certificati selezionati
      const selectedCertificatesData = certificates.filter((certificate) =>
        selectedCertificateIds.includes(certificate.id),
      );

      const response = await axiosInstance.put(
        `${BE_BASE_URL}/certificates/imported`,
        selectedCertificatesData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      /*
       L'esito e' per singolo attestato: il lotto non e' piu' atomico, e la risposta arriva
       200 anche quando qualcuno non passa. Vanno detti tutti e due i lati — quanti sono
       entrati e quali no — perche' un messaggio solo lascia l'operatore a indovinare che
       fine abbiano fatto gli altri, che era proprio il difetto da correggere.
      */
      const esito: CertificateValidationResult = response.data;

      if (esito.validati.length > 0) {
        toast.success(
          t("attestati-validazione-riuscita", {
            count: esito.validati.length,
          }),
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
          },
        );
      }

      if (esito.rifiutati.length > 0) {
        const intestazione = t("attestati-validazione-rifiutati", {
          count: esito.rifiutati.length,
        });
        toast.warning(
          `${intestazione}
${descriviAvvisi(esito.rifiutati)}`,
          AVVISO_CON_ELENCO,
        );
      }

      /*
       La selezione resta sui soli rifiutati: sono quelli su cui c'e' ancora da lavorare, e
       ritrovarseli gia' spuntati risparmia di ripescarli a mano in un elenco di cinquanta.
      */
      setSelectedCertificateIds(esito.rifiutati.map((r) => r.id));
      setAllSelected(false);
      fetchCertificates();
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const error = e as AxiosError;

        if (error.response) {
          // Accedi alla proprietà data come any per evitare errori di tipo
          const errorMessage = (error.response.data as any).message;
          toast.error(
            errorMessage || "Errore durante l'aggiornamento degli attestati.",
            {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: "colored",
              transition: Bounce,
            },
          );
        } else {
          toast.error("Errore durante l'aggiornamento degli attestati.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
          });
        }
      } else {
        toast.error(
          "Errore sconosciuto durante l'aggiornamento degli attestati.",
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
          },
        );
      }
    }
  };

  const deleteSelectedCertificates = async () => {
    if (solaLettura) return;
    if (selectedCertificateIds.length === 0) {
      toast.info("Seleziona almeno un certificato da eliminare.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
      return;
    }

    setAzioneDiLotto("eliminazione");
  };

  const eliminaSelezionati = async () => {
    try {
      await axiosInstance.delete(`${BE_BASE_URL}/certificates/imported`, {
        params: { ids: selectedCertificateIds.join(",") },
      });
      toast.success("Certificati eliminati con successo!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
      fetchCertificates(); // Richiedi la lista aggiornata dei certificati
      setSelectedCertificateIds([]); // Pulire le selezioni
      setAllSelected(false); // Resettare lo stato di selezione globale
    } catch (e) {
      toast.error(
        messaggioDiErrore(
          e,
          "Si è verificato un errore durante l'eliminazione.",
        ),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        },
      );
    }
  };

  const handleFileUpdate = (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setPdfPreview(reader.result as string); // Imposta l'anteprima PDF
      };
      reader.readAsDataURL(file); // Leggi il file come Data URL
    }
  };

  // Effetto per aggiungere l'event listener al file input
  useEffect(() => {
    if (updateFileInputRef.current) {
      updateFileInputRef.current.addEventListener("change", handleFileUpdate);
    }

    return () => {
      if (updateFileInputRef.current) {
        updateFileInputRef.current.removeEventListener(
          "change",
          handleFileUpdate,
        );
      }
    };
  }, []);

  const selectStyle = {
    "--padBlock": "0.5rem",
    border: "2px solid",
    borderRadius: "1rem",
    fontSize: "1rem",
    borderColor: corsoDaScegliere(selectedCertificate?.stato)
      ? Color.RED
      : Color.LIGHT_BLUE,
  } as React.CSSProperties;

  return (
    <>
      {/*
        Un caricamento puo' produrre piu' avvisi in fila (scartati, segnalazioni, ed errori
        per singolo file): senza limite si sovrappongono e quelli in fondo escono dallo
        schermo. Con il limite i successivi aspettano che si chiuda il precedente.
      */}
      <ToastContainer limit={3} />
      <ResetCss>
        <Loader show={loading.show}>{loading.message}</Loader>
        <PageWrapper
          ref={contenitoreRef}
          style={{ height: altezzaPagina, marginTop: RECUPERO_ARIA_NAVBAR }}
        >
          <TitoloPagina>{t("titolo-pagina-attestati-pregressi")}</TitoloPagina>

          <input
            id="file-upload"
            type="file"
            multiple
            accept=".xls,.xlsx"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              accodaFile(Array.from(e.target.files ?? []), "xlsx");
              e.target.value = "";
            }}
          />

          <FasciaCaricamento>
            {/*
              Trascinamento e file in coda stanno nello stesso box: il secondo è il
              risultato del primo, e da riquadri separati non si capiva.
            */}
            <BoxCaricamento
              id="uploaded-files-container"
              htmlFor="scelta-attestati-pdf"
              $sopra={trascinamentoSopra}
              onDragOver={(e) => {
                e.preventDefault();
                setTrascinamentoSopra(true);
              }}
              onDragLeave={() => setTrascinamentoSopra(false)}
              onDrop={handleDrop}
            >
              {/*
                La finestra di scelta si apre da sola perché il box è un `<label>` legato a
                questo input: nessun `click()` da codice. Aprirla a mano richiede un gesto
                utente ancora valido, e bastava un dettaglio storto — il valore riscritto un
                istante prima, un secondo evento in arrivo — perché non si aprisse o si
                riaprisse subito dopo la scelta, buttando via il file appena scelto.

                L'input è nascosto alla vista ma non al fuoco: con `display: none` non si
                raggiungerebbe con il tabulatore, e la pagina sarebbe usabile solo col mouse.
              */}
              <input
                id="scelta-attestati-pdf"
                aria-label={t("previous-certificates-scegli-pdf")}
                type="file"
                multiple
                accept=".pdf"
                style={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  opacity: 0,
                  pointerEvents: "none",
                }}
                onChange={(e) => {
                  accodaFile(Array.from(e.target.files ?? []), "pdf");
                  /*
                   Azzerare qui, dopo aver letto i file, e' cio' che permette di riscegliere
                   lo stesso file: il browser manda `change` solo se il valore cambia.
                   */
                  e.target.value = "";
                }}
              />
              <ZonaTrascinamento>
                <span style={{ fontSize: "1.1rem" }}>⬇</span>
                <span>Trascina qui gli attestati in PDF</span>
                <span className="tenue">oppure fai clic per sceglierli</span>
                {uploadedFiles.length > 0 && (
                  <ConteggioInCoda>
                    {uploadedFiles.length}{" "}
                    {uploadedFiles.length === 1 ? "in coda" : "in coda"}
                  </ConteggioInCoda>
                )}
              </ZonaTrascinamento>

              {uploadedFiles.length > 0 && (
                <FileInCoda id="uploaded-files-list">
                  {uploadedFiles.map(({ file }, index) => (
                    <PastigliaFile key={`file_upload_${index}`}>
                      <span title={file.name}>{file.name}</span>
                      <button
                        type="button"
                        aria-label={`Togli ${file.name} dalla coda`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          color: Color.RED,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          flexShrink: 0,
                        }}
                        onClick={(e) => {
                          /*
                           Il box e' un `<label>`: senza fermare l'evento, togliere un file
                           dalla coda aprirebbe anche la finestra di scelta.
                          */
                          e.preventDefault();
                          e.stopPropagation();
                          setUploadedFiles((precedenti) =>
                            precedenti.filter((_, i) => i !== index),
                          );
                        }}
                      >
                        <CircleX width={14} height={14} />
                      </button>
                    </PastigliaFile>
                  ))}
                </FileInCoda>
              )}
            </BoxCaricamento>

            <Suggerimento
              testo={t(solaLettura ? "sola-lettura-comando" : "previous-certificates-aiuto-elabora")}
            >
              <Button
                label="Elabora Attestati"
                color={Color.GREEN}
                icon={Icons.BUTTONS.EDIT}
                onClick={uploadFiles}
                disabled={solaLettura}
              />
            </Suggerimento>
          </FasciaCaricamento>

          {MOSTRA_CARICAMENTO_EXCEL && (
            <UploadBtnWrapper>
              <Button
                label="Carica XSLX"
                color={Color.BLUE}
                icon={Icons.BUTTONS.UPLOAD}
                onClick={apriSceltaExcel}
                disabled={solaLettura}
              />
              <Button
                label="Scarica Template"
                color={Color.BLUE}
                icon={Icons.BUTTONS.DOWNLOAD}
                onClick={() => handleDownloadTemplate()}
              />
            </UploadBtnWrapper>
          )}

          <ClickWrapper id="click-inside-wrapper">
            <Colonne>
              <ColonnaLista>
                <TableWrapper id="certificates-list">
                  <SubTitle
                    style={{
                      fontSize: "0.85rem",
                      letterSpacing: "0.08em",
                      padding: "0.6rem 0 0.3rem",
                    }}
                  >
                    ATTESTATI DA VALIDARE
                  </SubTitle>
                  <TableHeader>
                    <input
                      type="checkbox"
                      aria-label={t("previous-certificates-seleziona-tutti")}
                      defaultChecked={allSelected}
                      onChange={handleSelectAll}
                    />
                    <span>SELEZIONA TUTTI</span>
                  </TableHeader>

                  <ListWrapper>
                    {certificates &&
                      certificates.map((singleCertificate) => {
                        const isSelected = selectedCertificateIds.includes(
                          singleCertificate.id,
                        );
                        return (
                          <TableRow
                            role={"row"}
                            tabIndex={0}
                            key={`attestato_${singleCertificate.id}`}
                            onClick={() => handleRowSelect(singleCertificate)}
                            onKeyUp={(e) => {
                              if (e.key === "Enter") {
                                handleRowSelect(singleCertificate);
                              }
                            }}
                            $selected={
                              selectedCertificate?.id == singleCertificate.id
                            }
                            $state={singleCertificate.stato}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <input
                                type="checkbox"
                                aria-label={t(
                                  "previous-certificates-seleziona-riga",
                                  {
                                    attestato: `${singleCertificate.cognome} ${singleCertificate.nome} - ${singleCertificate.titoloCorso}`,
                                  },
                                )}
                                checked={isSelected}
                                onChange={() =>
                                  toggleCertificateSelection(
                                    singleCertificate.id,
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <p>{singleCertificate.cognome}</p>
                            <p>{singleCertificate.nome}</p>
                            <p>{singleCertificate.titoloCorso}</p>
                            <p>{singleCertificate.enteFormatore}</p>
                          </TableRow>
                        );
                      })}
                  </ListWrapper>
                </TableWrapper>

                {/* In colonna stretta le etichette per intero non ci stanno: font ridotto
                    e, se ancora non bastasse, i due bottoni vanno a capo invece di
                    tagliarsi la scritta. */}
                <ButtonWrapper
                  style={{
                    margin: 0,
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    flex: "0 0 auto",
                  }}
                >
                  <Suggerimento testo={t("previous-certificates-aiuto-elimina-selezionati")}>
                    <Button
                      label="Elimina Selezionati"
                      color={Color.RED}
                      fontSize="11px"
                      onClick={deleteSelectedCertificates}
                      disabled={solaLettura}
                    />
                  </Suggerimento>
                  <Suggerimento testo={t("previous-certificates-aiuto-valida-selezionati")}>
                    <Button
                      label="Valida Selezionati"
                      color={Color.BLUE}
                      fontSize="11px"
                      onClick={validateAll}
                      disabled={solaLettura}
                    />
                  </Suggerimento>
                </ButtonWrapper>
              </ColonnaLista>

              <DataWrapper>
                <LeftSectionWrapper>
                  <SubTitle
                    style={{
                      fontSize: "1rem",
                      padding: 0,
                      textAlign: "left",
                      marginBottom: "0.6rem",
                    }}
                  >
                    Verifica e correggi i dati estratti dall&apos;AI
                  </SubTitle>
                  <FormWrapper
                    id="update-certificate-form"
                    ref={formWrapperRef}
                    onSubmit={chiediConfermaValidazione}
                  >
                    <input
                      type="hidden"
                      name="certificate-id"
                      defaultValue={selectedCertificate?.id}
                    />
                    <InputField>
                      <label htmlFor="nome">Nome:</label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        placeholder="Il nome del partecipante"
                        value={formData.nome}
                        onChange={(e) =>
                          setFormData({ ...formData, nome: e.target.value })
                        }
                        required
                      />
                    </InputField>
                    <InputField>
                      <label>Cognome:</label>
                      <input
                        type="text"
                        id="cognome"
                        name="cognome"
                        placeholder="Il cognome del partecipante"
                        value={formData.cognome}
                        onChange={(e) =>
                          setFormData({ ...formData, cognome: e.target.value })
                        }
                        required
                      />
                    </InputField>
                    <InputField>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          color: noteCampi.cf ? Color.RED : "inherit",
                        }}
                      >
                        Codice fiscale:
                        {noteCampi.cf && (
                          <TriangleAlert
                            color={Color.RED}
                            height={16}
                            width={16}
                          />
                        )}
                      </label>
                      <input
                        type="text"
                        id="codiceFiscaleDiscente"
                        name="codiceFiscaleDiscente"
                        placeholder="Il CF del partecipante"
                        value={formData.cf}
                        onChange={(e) => {
                          dimenticaErroreSu("cf");
                          setFormData({ ...formData, cf: e.target.value });
                        }}
                        style={{
                          borderColor: noteCampi.cf
                            ? Color.RED
                            : Color.LIGHT_BLUE,
                        }}
                        required
                      />
                      {noteCampi.cf && <NotaCampo>{noteCampi.cf}</NotaCampo>}
                    </InputField>

                    <InputField title={selectedCourse?.name ?? ""}>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          color: noteCampi.corso ? Color.RED : "inherit",
                        }}
                      >
                        Corso:{" "}
                        {noteCampi.corso && (
                          <TriangleAlert
                            color={Color.RED}
                            height={16}
                            width={16}
                          />
                        )}
                      </label>

                      <Ariakit.ComboboxProvider
                        resetValueOnHide
                        setValue={(value) => {
                          startTransition(() => {
                            setSearchCourseValue(value);
                          });
                        }}
                      >
                        <SelectProvider store={courseStore}>
                          <Select
                            style={selectStyle}
                            sameWidth={false}
                            combobox
                            renderValue={(value) => {
                              return value
                                ? courses?.get(+value)?.name
                                : "Seleziona un corso...";
                            }}
                          >
                            {courses ? renderCourseSelectItems() : null}
                          </Select>
                        </SelectProvider>
                      </Ariakit.ComboboxProvider>
                      {noteCampi.corso && (
                        <NotaCampo>{noteCampi.corso}</NotaCampo>
                      )}
                    </InputField>
                    <InputField>
                      <label
                        style={{
                          color: noteCampi.dataValidita ? Color.RED : "inherit",
                        }}
                      >
                        Data scadenza:
                      </label>
                      <input
                        type="date"
                        id="data_scadenza"
                        name="dataValidita"
                        placeholder="Data di scadenza"
                        value={formData.dataValidita}
                        style={{
                          borderColor: noteCampi.dataValidita
                            ? Color.RED
                            : Color.LIGHT_BLUE,
                        }}
                        onChange={(e) => {
                          dimenticaErroreSu("dataValidita");
                          setFormData({
                            ...formData,
                            dataValidita: e.target.value,
                          });
                        }}
                      />
                      {noteCampi.dataValidita && (
                        <NotaCampo>{noteCampi.dataValidita}</NotaCampo>
                      )}
                    </InputField>
                    <InputField>
                      <label
                        style={{
                          color: noteCampi.inizioCorso ? Color.RED : "inherit",
                        }}
                      >
                        Data emissione attestato:
                      </label>
                      <input
                        type="date"
                        id="data_inizio_corso"
                        name="inizioCorso"
                        placeholder="Data di inizio del corso"
                        value={formData.inizioCorso}
                        style={{
                          borderColor: noteCampi.inizioCorso
                            ? Color.RED
                            : Color.LIGHT_BLUE,
                        }}
                        onChange={(e) => {
                          dimenticaErroreSu("inizioCorso");
                          setFormData({
                            ...formData,
                            inizioCorso: e.target.value,
                          });
                        }}
                        required
                      />
                      {noteCampi.inizioCorso && (
                        <NotaCampo>{noteCampi.inizioCorso}</NotaCampo>
                      )}
                    </InputField>
                    <InputField>
                      <label
                        style={{
                          color: noteCampi.fineCorso ? Color.RED : "inherit",
                        }}
                      >
                        Data fine corso:
                      </label>
                      <input
                        type="date"
                        id="data_fine_corso"
                        name="fineCorso"
                        placeholder="Data di fine del corso"
                        value={formData.fineCorso}
                        style={{
                          borderColor: noteCampi.fineCorso
                            ? Color.RED
                            : Color.LIGHT_BLUE,
                        }}
                        onChange={(e) => {
                          dimenticaErroreSu("fineCorso");
                          setFormData({
                            ...formData,
                            fineCorso: e.target.value,
                          });
                        }}
                      />
                      {noteCampi.fineCorso && (
                        <NotaCampo>{noteCampi.fineCorso}</NotaCampo>
                      )}
                    </InputField>
                    <InputField>
                      <label>Ente formatore:</label>
                      <input
                        type="text"
                        id="ente_formatore"
                        name="enteFormatore"
                        placeholder="L'ente che ha rilasciato l'attestato"
                        value={formData.enteFormatore}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            enteFormatore: e.target.value,
                          })
                        }
                        required
                      />
                    </InputField>
                    <InputField>
                      <label>Codice attestato:</label>
                      <input
                        type="text"
                        id="codice_attestato"
                        name="codiceAttestato"
                        placeholder="Il codice identificativo dell'attestato"
                        value={formData.codiceAttestato}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            codiceAttestato: e.target.value,
                          })
                        }
                      />
                    </InputField>
                    <InputField>
                      <label htmlFor="file">Sostituisci attestato:</label>
                      <Suggerimento testo={t("previous-certificates-sostituisci-pdf")}>
                        <input
                          type="file"
                          id="file"
                          name="file"
                          accept=".pdf"
                          ref={updateFileInputRef}
                        />
                      </Suggerimento>
                    </InputField>
                    <ButtonWrapper>
                      <Suggerimento testo={t("previous-certificates-aiuto-valida")}>
                        <SubmitButton
                          label="VALIDA"
                          form="update-certificate-form"
                          color={Color.BLUE}
                          name="SubmitButton"
                          style={{
                            padding: "0.3rem 1rem",
                            opacity: solaLettura ? 0.5 : 1,
                          }}
                          disabled={solaLettura}
                        />
                      </Suggerimento>
                    </ButtonWrapper>
                  </FormWrapper>
                </LeftSectionWrapper>
                <RightSectionWrapper>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <SubTitle style={{ fontSize: "1rem", padding: 0 }}>
                      Anteprima PDF
                    </SubTitle>
                  </div>
                  <PdfPreviewWrapper id="pdf-preview">
                    <VisorePdf
                      sorgente={
                        pdfPreview ??
                        (selectedCertificate?.fileData
                          ? `data:application/pdf;base64,${selectedCertificate.fileData}`
                          : null)
                      }
                    />
                  </PdfPreviewWrapper>
                </RightSectionWrapper>
              </DataWrapper>
            </Colonne>
          </ClickWrapper>
        </PageWrapper>
        <DialogConferma
          aperta={validazioneInAttesa !== null}
          titolo="Conferma validazione"
          messaggio="I dati inseriti sono corretti? L'attestato verrà validato e caricato in archivio."
          etichettaConferma="SÌ, VALIDA"
          onConferma={() => {
            const inAttesa = validazioneInAttesa;
            setValidazioneInAttesa(null);
            if (inAttesa) {
              updateCertificate(inAttesa.formData, inAttesa.file);
            }
          }}
          onAnnulla={() => setValidazioneInAttesa(null)}
        />
        <DialogConferma
          aperta={azioneDiLotto !== null}
          titolo={
            azioneDiLotto === "eliminazione"
              ? "Elimina attestati"
              : "Conferma validazione"
          }
          messaggio={
            azioneDiLotto === "eliminazione"
              ? "Sei sicuro di voler eliminare i certificati selezionati?"
              : "Si è sicuri di continuare l'operazione e se ne assume la responsabilità?"
          }
          etichettaConferma={
            azioneDiLotto === "eliminazione" ? "ELIMINA" : "SÌ, VALIDA"
          }
          coloreConferma={
            azioneDiLotto === "eliminazione" ? Color.RED : Color.BLUE
          }
          coloreAnnulla={
            azioneDiLotto === "eliminazione" ? Color.BLUE : Color.RED
          }
          onConferma={() => {
            const azione = azioneDiLotto;
            setAzioneDiLotto(null);
            if (azione === "validazione") validaSelezionati();
            if (azione === "eliminazione") eliminaSelezionati();
          }}
          onAnnulla={() => setAzioneDiLotto(null)}
        />
      </ResetCss>
    </>
  );
};

export default PreviousCertificatesPage;
